import { createStore } from '@xstate/store-react';
import { AppRoute } from '../enums/appRoutes';
import { initialiseStore } from './events/initialise';
import { createSession } from './events/session/create';
import { setSession } from './events/session/set';
import { initialContext } from './initial';
import { sessionFailure } from './events/session/failure';
import { getBrandCatalogue } from './events/catalogue/get';
import { setBrandCatalogue } from './events/catalogue/set';
import { catalogueFailed } from './events/catalogue/failed';
import { processUrlParams } from './events/url/process';
import { setParamResult } from './events/url/setParams';
import { back } from './events/back';
import { next } from './events/next';
import { saveModelLookupResult } from './events/url/saveResults';
import { submitRegistration } from './events/regAndQuote/submit';
import { quoteFailed } from './events/quote/failed';
import { setQuote } from './events/quote/set';
import { noQuoteRegOnly } from './events/quote/noQuoteRegOnly';
import { regOnly } from './events/quote/regOnly';
import { continueToCheckout } from './events/checkout/continue';
import { setCheckout } from './events/checkout/set';
import { checkoutSuccess } from './events/checkout/success';
import { checkoutFailed } from './events/checkout/failed';
import { startSessionTimer } from './events/session/startTimer';
import { sessionExpired } from './events/session/expired';
import { restoreSession } from './events/session/restore';
import { StoreEvent } from '../enums/storeEvent';
import { EventHandler } from '../models/store/eventHander';
import { quoteError } from './events/quote/error';

const createAppStore = () =>
  createStore({
    context: initialContext,
    emits: {
      navigate: (_payload: { path: AppRoute; replace?: boolean }): void => {},
    },
    on: {
      [StoreEvent.INITIALISE]: initialiseStore,
      /* Session Events */
      [StoreEvent.CREATE_SESSION]: createSession,
      [StoreEvent.SET_SESSION]: setSession,
      [StoreEvent.SESSION_CREATION_FAILED]: sessionFailure,
      [StoreEvent.START_SESSION_TIMER]: startSessionTimer,
      [StoreEvent.SESSION_EXPIRED]: sessionExpired,
      [StoreEvent.RESTORE_SESSION]: restoreSession,
      /* Catalogue Events */
      [StoreEvent.GET_CATALOGUE]: getBrandCatalogue,
      [StoreEvent.SET_CATALOGUE]: setBrandCatalogue,
      [StoreEvent.GET_CATALOGUE_FAILED]: catalogueFailed,
      /** URL Params Processing */
      [StoreEvent.PROCESS_URL_PARAMS]: processUrlParams,
      [StoreEvent.SET_URL_PARAM_RESULTS]: setParamResult,
      [StoreEvent.SAVE_APPLIANCE_MODEL_RESULTS]: saveModelLookupResult,
      /** Form Navigation */
      [StoreEvent.NEXT]: next,
      [StoreEvent.BACK]: back,
      /** Registration and Quote Events */
      [StoreEvent.SUBMIT]: submitRegistration,
      [StoreEvent.QUOTE_FAILED]: quoteFailed,
      [StoreEvent.QUOTE_ERROR]: quoteError,
      [StoreEvent.SET_QUOTE]: setQuote,
      [StoreEvent.NO_QUOTE_REG_ONLY]: noQuoteRegOnly,
      [StoreEvent.REG_ONLY]: regOnly,
      /** Checkout Events */
      [StoreEvent.CONTINUE_TO_CHECKOUT]: continueToCheckout,
      [StoreEvent.SET_CHECKOUT]: setCheckout,
      [StoreEvent.CHECKOUT_SUCCESS]: checkoutSuccess,
      [StoreEvent.CHECKOUT_FAILED]: checkoutFailed,
    } satisfies EventHandler,
  });

// ---------------------------------------------------------------------------
// Singleton with HMR support
// ---------------------------------------------------------------------------

let storeInstance: ReturnType<typeof createAppStore> | undefined;

if (import.meta.hot?.data?.storeInstance) {
  storeInstance = import.meta.hot.data.storeInstance;
}

if (!storeInstance) {
  storeInstance = createAppStore();
  if (import.meta.hot?.data) {
    import.meta.hot.data.storeInstance = storeInstance;
  }
}

export const store = storeInstance;
